import { Component } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router, RouterLink } from '@angular/router';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatCard, MatCardHeader, MatCardTitle, MatCardSubtitle, MatCardContent } from '@angular/material/card';
import { MatFormField, MatLabel, MatError, MatInput } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { AID_YEARS } from '../_shared/aid-year/aid-year.config';
import { CreateSchoolService } from '../_services/create-school.service';
import { normalizeSchoolCode, schoolCodeValidator } from '../_services/create-school.builder';

@Component({
  selector: 'app-create-school',
  templateUrl: './create-school.component.html',
  styleUrls: ['./create-school.component.css'],
  imports: [
    ReactiveFormsModule,
    RouterLink,
    MatButton,
    MatIcon,
    MatCard,
    MatCardHeader,
    MatCardTitle,
    MatCardSubtitle,
    MatCardContent,
    MatFormField,
    MatLabel,
    MatError,
    MatInput,
    MatSelect,
    MatOption,
  ],
})
export class CreateSchoolComponent {
  readonly aidYears = AID_YEARS;
  saving = false;
  errorMessage = '';

  readonly form = this.fb.nonNullable.group({
    code: ['', [Validators.required, schoolCodeValidator()]],
    name: ['', Validators.required],
    institutionalMethod: [false, Validators.required],
    orgId: ['12345', Validators.required],
    aidYear: [AID_YEARS[0].code, Validators.required],
  });

  constructor(
    private fb: FormBuilder,
    private createSchoolService: CreateSchoolService,
    private router: Router,
    title: Title,
  ) {
    title.setTitle('Create a school | NPC Admin');
  }

  onCodeInput(event: Event): void {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) {
      return;
    }
    const normalized = normalizeSchoolCode(input.value);
    if (normalized !== input.value) {
      this.form.controls.code.setValue(normalized);
    }
  }

  codeError(): string | null {
    const control = this.form.controls.code;
    if (!control.touched && !control.dirty) {
      return null;
    }
    return (control.getError('schoolCode') as string | undefined) ?? null;
  }

  async submit(): Promise<void> {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.errorMessage = this.codeError() ?? 'Complete the required fields.';
      return;
    }

    this.saving = true;
    this.errorMessage = '';
    const value = this.form.getRawValue();
    try {
      const code = await this.createSchoolService.createSchool({
        code: normalizeSchoolCode(value.code),
        name: value.name,
        institutionalMethod: value.institutionalMethod,
        orgId: value.orgId,
        aidYear: value.aidYear,
      });
      await this.router.navigate(['/', code, 'dashboard']);
    } catch (err) {
      this.errorMessage = err instanceof Error ? err.message : 'Unable to create the school.';
      this.saving = false;
    }
  }
}
